import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  HelpCircle,
  MessagesSquare,
  PanelLeftClose,
  PanelLeftOpen,
  PencilLine,
  SquareCheckBig,
  SquareDashed,
  X,
} from "lucide-react";
import {
  useAronApiBase,
  useAronConversations,
  useAronPendingWriteThreads,
} from "../../api/aronApi";
import { useAronUiStore } from "../../store/aronUiStore";
import {
  collectGroups,
  findGroup,
  useAronWorkbenchStore,
} from "../../store/aronWorkbenchStore";
import { AronConversationList } from "../AronConversationList";
import { AronEditorGroup } from "./AronEditorGroup";
import { AronSplitView } from "./AronSplitView";

/** Desktop = the split layout is meaningful; below that the workbench shows one
 * pane at a time (tabs still work, dividers do not). */
function useIsDesktop(): boolean {
  const [is, setIs] = useState(
    () => typeof window === "undefined" || window.matchMedia("(min-width: 768px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const on = () => setIs(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return is;
}

/**
 * The ARON workbench — conversations as tabs, tabs in panes you can split and
 * resize, plus the status bar that says out loud what ARON is waiting for.
 */
export function AronWorkbench() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isDesktop = useIsDesktop();

  const root = useAronWorkbenchStore((s) => s.root);
  const activeGroupId = useAronWorkbenchStore((s) => s.activeGroupId);
  const maximizedGroupId = useAronWorkbenchStore((s) => s.maximizedGroupId);
  const sidebarOpen = useAronWorkbenchStore((s) => s.sidebarOpen);
  const setSidebarOpen = useAronWorkbenchStore((s) => s.setSidebarOpen);
  const openConversation = useAronWorkbenchStore((s) => s.openConversation);
  const openAuditConversation = useAronWorkbenchStore((s) => s.openAuditConversation);
  const openDraft = useAronWorkbenchStore((s) => s.openDraft);
  const setDraggingTab = useAronWorkbenchStore((s) => s.setDraggingTab);
  const openDock = useAronUiStore((s) => s.openDock);

  const [mobileList, setMobileList] = useState(false);

  const groups = useMemo(() => collectGroups(root), [root]);
  const activeGroup = findGroup(root, activeGroupId) ?? groups[0];
  const activeTab = activeGroup?.tabs.find((t) => t.key === activeGroup.activeKey);
  // Podgląd cudzej rozmowy (audyt) NIE trafia do `?c=`: ten parametr otwiera
  // rozmowę jako czat, a cudzego wątku zwykłe endpointy nie wydadzą (404).
  const activeConvId = activeTab?.readOnly ? null : (activeTab?.convId ?? null);

  // ── URL ↔ active tab ──────────────────────────────────────────────────────
  // `?c=` is the deep link into a conversation (sidebar badges, notifications,
  // an old bookmark). It opens as a tab; from then on the URL follows the tab.
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    const c = params.get("c");
    if (c) openConversation(c);
  }, [params, openConversation]);

  useEffect(() => {
    if (!didInit.current) return;
    if ((params.get("c") ?? null) === activeConvId) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (activeConvId) next.set("c", activeConvId);
        else next.delete("c");
        return next;
      },
      { replace: true },
    );
  }, [activeConvId, params, setParams]);

  // Last line of defence for the drop overlays: whatever happened to the dragged
  // tab (it may have been re-parented mid-drop), the drag is over once the
  // browser says so.
  useEffect(() => {
    const clear = () => setDraggingTab(null);
    window.addEventListener("dragend", clear);
    window.addEventListener("drop", clear);
    return () => {
      window.removeEventListener("dragend", clear);
      window.removeEventListener("drop", clear);
    };
  }, [setDraggingTab]);

  const popOut = (convId: string) => {
    // Named target: tearing the same conversation out twice focuses the window
    // that already holds it instead of opening a second copy.
    const w = window.open(`/aron?c=${convId}&popout=1`, `aron-${convId}`, "width=560,height=860");
    w?.focus();
  };
  const detachToDock = (convId: string) => {
    openDock(convId);
    navigate("/");
  };

  const select = (id: string | null) => {
    if (id) openConversation(id);
    else openDraft();
    setMobileList(false);
  };

  // Maximised pane, or (on a phone) just the active one — otherwise the tree.
  const solo =
    (maximizedGroupId && findGroup(root, maximizedGroupId)) ||
    (!isDesktop ? activeGroup : null);

  return (
    <div className="-mx-4 -my-4 flex h-[calc(100dvh-3.5rem)] overflow-hidden lg:-mx-6 lg:-my-6">
      {sidebarOpen && (
        <aside className="hidden w-64 shrink-0 border-r border-gray-200 md:flex md:flex-col dark:border-gray-700">
          <AronConversationList
            selectedId={activeTab?.convId ?? null}
            onSelect={(id) => openConversation(id)}
            onNew={() => openDraft()}
            onSelectAudit={(c) =>
              openAuditConversation(c.id, c.title || "(bez tytułu)")
            }
          />
        </aside>
      )}

      {/* Mobile drawer — the sidebar is hidden on small screens. */}
      {mobileList && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileList(false)} />
          <div className="absolute left-0 top-0 flex h-full w-72 max-w-[85%] flex-col bg-white shadow-xl dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-gray-700">
              <span className="text-sm font-medium">Rozmowy</span>
              <button
                type="button"
                onClick={() => setMobileList(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <AronConversationList
                selectedId={activeTab?.convId ?? null}
                onSelect={select}
                onNew={() => select(null)}
                onSelectAudit={(c) => {
                  openAuditConversation(c.id, c.title || "(bez tytułu)");
                  setMobileList(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1">
          {solo ? (
            <AronEditorGroup
              group={solo}
              isActiveGroup
              groupCount={groups.length}
              onPopOut={popOut}
              onDetach={detachToDock}
            />
          ) : (
            <AronSplitView
              node={root}
              groupCount={groups.length}
              onPopOut={popOut}
              onDetach={detachToDock}
            />
          )}
        </div>
        <AronStatusBar
          onOpenList={() => setMobileList(true)}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
        />
      </div>
    </div>
  );
}

/**
 * Bottom strip, VS Code style: icons first, one line, never wraps. It carries
 * exactly what ARON is waiting for — one click per item takes you there.
 */
function AronStatusBar({
  onOpenList,
  onToggleSidebar,
  sidebarOpen,
}: {
  onOpenList: () => void;
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
}) {
  const { data: convs = [] } = useAronConversations();
  const { data: pendingThreads = [] } = useAronPendingWriteThreads();
  const attention = useAronWorkbenchStore((s) => s.attention);
  const root = useAronWorkbenchStore((s) => s.root);
  const activateTab = useAronWorkbenchStore((s) => s.activateTab);
  const approvalModalEnabled = useAronWorkbenchStore((s) => s.approvalModalEnabled);
  const setApprovalModalEnabled = useAronWorkbenchStore((s) => s.setApprovalModalEnabled);
  const openApproval = useAronWorkbenchStore((s) => s.openApproval);
  const apiBase = useAronApiBase();

  const titleOf = (convId: string) =>
    convs.find((c) => c.id === convId)?.title ||
    pendingThreads.find((t) => t.id === convId)?.title ||
    "Rozmowa";

  const waiting = useMemo(
    () =>
      Object.entries(attention)
        .filter(([, kind]) => kind === "question" || kind === "write")
        .map(([convId, kind]) => ({ convId, kind, title: titleOf(convId) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attention, convs, pendingThreads],
  );

  // Proposals waiting in the conversations that are actually open here.
  const pending = useMemo(() => {
    const open = new Set(
      collectGroups(root).flatMap(
        (g) => g.tabs.map((t) => t.convId).filter(Boolean) as string[],
      ),
    );
    const rows = convs.filter((c) => open.has(c.id) && (c.pending_writes_count ?? 0) > 0);
    return {
      total: rows.reduce((acc, c) => acc + (c.pending_writes_count ?? 0), 0),
      firstConvId: rows[0]?.id ?? null,
    };
  }, [convs, root]);

  const focus = (convId: string) => {
    for (const g of collectGroups(root)) {
      const hit = g.tabs.find((t) => t.convId === convId);
      if (hit) {
        activateTab(g.id, hit.key);
        return;
      }
    }
  };

  const item =
    "inline-flex h-5 shrink-0 items-center gap-1 px-1.5 transition-colors hover:bg-gray-200 dark:hover:bg-gray-800";
  const label = approvalModalEnabled
    ? "Prośby o zapis do bazy: ARON pyta OKNEM na wierzchu. Kliknij, aby dostawać je tylko jako kartę w rozmowie."
    : "Prośby o zapis do bazy: tylko karta w rozmowie. Kliknij, aby ARON pytał oknem na wierzchu.";

  return (
    <div className="flex h-6 shrink-0 items-center gap-1 overflow-hidden border-t border-gray-200 bg-gray-100 px-1 text-[11px] text-gray-500 dark:border-gray-700 dark:bg-gray-950">
      <button
        type="button"
        onClick={onToggleSidebar}
        title={sidebarOpen ? "Ukryj listę rozmów" : "Pokaż listę rozmów"}
        className={`${item} hidden md:inline-flex`}
      >
        {sidebarOpen ? <PanelLeftClose size={13} /> : <PanelLeftOpen size={13} />}
      </button>
      <button type="button" onClick={onOpenList} className={`${item} md:hidden`}>
        <MessagesSquare size={13} />
      </button>

      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
        {waiting.map((w) => (
          <button
            key={w.convId}
            type="button"
            onClick={() => {
              focus(w.convId);
              if (w.kind === "write") openApproval({ convId: w.convId, apiBase });
            }}
            title={
              w.kind === "question"
                ? `ARON zadał pytanie i czeka na odpowiedź — ${w.title}`
                : `ARON czeka na zatwierdzenie zapisu — ${w.title}`
            }
            className={`${item} font-medium ${
              w.kind === "question" ? "text-amber-600" : "text-red-600"
            }`}
          >
            {w.kind === "question" ? <HelpCircle size={12} /> : <PencilLine size={12} />}
            <span className="max-w-[12rem] truncate">{w.title}</span>
          </button>
        ))}
      </div>

      {pending.total > 0 && (
        <button
          type="button"
          onClick={() => {
            if (!pending.firstConvId) return;
            focus(pending.firstConvId);
            openApproval({ convId: pending.firstConvId, apiBase });
          }}
          title={`${pending.total} propozycji zapisu czeka na Twoją decyzję — otwórz okno`}
          className={`${item} font-mono font-semibold text-red-600`}
        >
          <PencilLine size={12} /> {pending.total}
        </button>
      )}

      <button
        type="button"
        onClick={() => setApprovalModalEnabled(!approvalModalEnabled)}
        aria-pressed={approvalModalEnabled}
        aria-label={label}
        title={label}
        className={item}
      >
        {approvalModalEnabled ? (
          <SquareCheckBig size={12} className="text-red-600" />
        ) : (
          <SquareDashed size={12} />
        )}
      </button>
    </div>
  );
}
